/**
 * EmptyStateOnboarding
 *
 * Welcome screen displayed when there is no Daily Brief data available.
 * Shows three informational cards explaining the main features: workout completion
 * monitoring, questionnaire alerts, and renewal planning. Includes a call-to-action
 * button to add athletes and a link to support documentation.
 *
 * @component
 * @param {Function} onAddAthletes - Callback function when "ADD ATHLETES" button is clicked
 */
import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import iconSet from '../../../shared/images/teambuildr-selection.json';

const Container = styled('div')`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 64px;
  gap: 64px;
  width: 100%;
  min-height: 776px;
  background: #FFFFFF;
`;

const TextContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  width: 100%;
  max-width: 728px;
  gap: 0;
`;

const Title = styled('h1')`
  width: 100%;
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 700;
  font-size: 28px;
  line-height: 150%;
  text-align: center;
  color: #444444;
  margin: 0 0 8px 0;
`;

const Subtitle = styled('p')`
  width: 100%;
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 19px;
  line-height: 150%;
  text-align: center;
  color: #9E9E9E;
  margin: 0;
`;

const CardsContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 40px;
  width: 100%;
  max-width: 1152px;
`;

const Card = styled('div')`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  gap: 16px;
  flex: 1;
  background: #FFFFFF;
  border: 1px solid #EEEEEE;
  box-shadow: 0px 16px 24px rgba(48, 49, 51, 0.1), 0px 0px 1px rgba(48, 49, 51, 0.05);
  border-radius: 8px;
`;

const IconContainer = styled('div')`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardTextContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  width: 100%;
`;

const CardTitle = styled('h3')`
  width: 100%;
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 700;
  font-size: 19px;
  line-height: 150%;
  text-align: center;
  color: #444444;
  margin: 0;
`;

const CardDescription = styled('p')`
  width: 100%;
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  text-align: center;
  color: #9E9E9E;
  margin: 0;
`;

const ButtonContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 8px;
  width: 100%;
  max-width: 500px;
`;

const PrimaryButton = styled('button')`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 6px 16px;
  gap: 10px;
  width: 100%;
  height: 32px;
  background: #444444;
  box-shadow: 0px 16px 24px rgba(48, 49, 51, 0.1), 0px 0px 1px rgba(48, 49, 51, 0.05);
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 150%;
  text-align: center;
  letter-spacing: 0.03em;
  color: #FFFFFF;
  transition: background-color 0.2s ease;

  &:hover {
    background: #333333;
  }

  &:active {
    background: #222222;
  }
`;

const EmptyStateOnboarding = ({ onAddAthletes }) => (
  <Container>
    <TextContainer>
      <Title>Welcome to Your Daily Brief</Title>
      <Subtitle>
        Stay on top of athlete performance with a quick, at-a-glance summary of
        workout completion, questionnaire alerts, and days left in programming.
      </Subtitle>
    </TextContainer>

    <CardsContainer>
      <Card>
        <IconContainer>
          <IcomoonReact
            iconSet={iconSet}
            color='#77A7FF'
            size={64}
            icon='pr-tracking'
          />
        </IconContainer>
        <CardTextContainer>
          <CardTitle>Monitor Workout Completion</CardTitle>
          <CardDescription>
            Monitor completion as athletes progress through their program.
          </CardDescription>
        </CardTextContainer>
      </Card>

      <Card>
        <IconContainer>
          <IcomoonReact
            iconSet={iconSet}
            color='#FF0505'
            size={64}
            icon='gs-alert'
          />
        </IconContainer>
        <CardTextContainer>
          <CardTitle>Receive Questionnaire Alerts</CardTitle>
          <CardDescription>
            Catch performance red flags before they become bigger issues.
          </CardDescription>
        </CardTextContainer>
      </Card>

      <Card>
        <IconContainer>
          <IcomoonReact
            iconSet={iconSet}
            color='#FFCB47'
            size={64}
            icon='clock'
          />
        </IconContainer>
        <CardTextContainer>
          <CardTitle>Plan Renewals</CardTitle>
          <CardDescription>
            Ensure clients and athletes stay on track and plan renewals in advance.
          </CardDescription>
        </CardTextContainer>
      </Card>
    </CardsContainer>

    <ButtonContainer>
      <PrimaryButton onClick={onAddAthletes}>
        ADD ATHLETES
      </PrimaryButton>
    </ButtonContainer>
  </Container>
);

EmptyStateOnboarding.propTypes = {
  onAddAthletes: PropTypes.func,
};

EmptyStateOnboarding.defaultProps = {
  onAddAthletes: () => {},
};

export default EmptyStateOnboarding;
